import axios from 'axios'
import {getAuth} from '../modules/auth/core/AuthHelpers'
import {analyticsPageName, isMeaningfulAnalyticsLabel} from './analyticsLabels'

type AnalyticsEvent = {
  event_guid: string
  session_id: string
  event_type: string
  event_name?: string | null
  path: string
  referrer?: string | null
  method?: string
  status?: number
  duration_ms?: number
  success?: boolean
  device: string
  browser: string
  resolution: string
  viewport: string
  language: string
  metadata?: Record<string, unknown>
  occurred_at: string
}

const QUEUE_KEY = 'iexp.analytics.queue.v1'
const SESSION_KEY = 'iexp.analytics.session.v1'
const SESSION_STARTED_KEY = 'iexp.analytics.session.started.v1'
const MAX_QUEUE = 250
const BATCH_SIZE = 100
const FLUSH_SIZE = 20
const FLUSH_INTERVAL = 5000
const HEARTBEAT_INTERVAL = 30000

const API_BASE_URL = String(process.env.REACT_APP_API_URL || axios.defaults.baseURL || window.location.origin)
  .replace(/\/$/, '')

let started = false
let flushing = false
let currentPath = normalizePagePath(window.location.pathname)
let currentScreenStartedAt = performance.now()
let flushTimer: number | undefined
let heartbeatTimer: number | undefined
let lastReferrer = document.referrer || null

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.random() * 16 | 0
    return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16)
  })
}

const getSessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = createId()
    sessionStorage.setItem(SESSION_KEY, id)
    sessionStorage.setItem(SESSION_STARTED_KEY, new Date().toISOString())
  }
  return id
}

const getSessionStartedAt = () => {
  const value = sessionStorage.getItem(SESSION_STARTED_KEY)
  if (value) return value
  getSessionId()
  return sessionStorage.getItem(SESSION_STARTED_KEY) || new Date().toISOString()
}

export function normalizePagePath(path: string) {
  const pathname = (path || '/').split('?')[0].split('#')[0] || '/'
  if (pathname === '/analytics' || pathname.startsWith('/analytics/')) return '/analytics'
  return pathname
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|$)/gi, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id')
}

const normalizeEndpoint = (url?: string) => {
  try {
    const base = API_BASE_URL
    return normalizePagePath(new URL(url || '/', base).pathname)
  } catch (_) {
    return normalizePagePath(url || '/')
  }
}

const getDevice = () => {
  const ua = navigator.userAgent.toLowerCase()
  if (/ipad|tablet|playbook|silk/.test(ua)) return 'Tablet'
  if (/mobile|iphone|ipod|android/.test(ua)) return 'Mobile'
  return 'Desktop'
}

const getBrowser = () => {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return `Edge ${ua.match(/Edg\/([\d.]+)/)?.[1] || ''}`.trim()
  if (/OPR\//.test(ua)) return `Opera ${ua.match(/OPR\/([\d.]+)/)?.[1] || ''}`.trim()
  if (/Chrome\//.test(ua)) return `Chrome ${ua.match(/Chrome\/([\d.]+)/)?.[1] || ''}`.trim()
  if (/Firefox\//.test(ua)) return `Firefox ${ua.match(/Firefox\/([\d.]+)/)?.[1] || ''}`.trim()
  if (/Safari\//.test(ua)) return `Safari ${ua.match(/Version\/([\d.]+)/)?.[1] || ''}`.trim()
  return 'Desconhecido'
}

const context = () => ({
  session_id: getSessionId(),
  path: currentPath,
  device: getDevice(),
  browser: getBrowser(),
  resolution: `${window.screen.width}x${window.screen.height}`,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  language: navigator.language || 'pt-BR',
})

const readQueue = (): AnalyticsEvent[] => {
  try {
    const value = localStorage.getItem(QUEUE_KEY)
    const parsed = value ? JSON.parse(value) : []
    return Array.isArray(parsed) ? parsed.slice(-MAX_QUEUE) : []
  } catch (_) {
    return []
  }
}

const writeQueue = (queue: AnalyticsEvent[]) => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)))
  } catch (_) {}
}

const isAnalyticsUrl = (url?: string) => normalizeEndpoint(url).startsWith('/analytics')

const endpointUrl = (path: string) => `${API_BASE_URL}${path}`

const authHeaders = () => {
  const token = getAuth()?.api_token
  return token ? {Authorization: token, 'Content-Type': 'application/json'} : null
}

export const enqueueAnalyticsEvent = (
  event: Omit<AnalyticsEvent, 'event_guid' | 'session_id' | 'path' | 'device' | 'browser' | 'resolution' | 'viewport' | 'language' | 'occurred_at'> & {path?: string}
) => {
  if (!getAuth()?.api_token) return
  const item: AnalyticsEvent = {
    event_guid: createId(),
    occurred_at: new Date().toISOString(),
    ...context(),
    ...event,
    path: normalizePagePath(event.path || currentPath),
  }
  const queue = readQueue()
  queue.push(item)
  writeQueue(queue)
  if (queue.length >= FLUSH_SIZE) flushAnalytics()
}

export const flushAnalytics = async (keepalive = false) => {
  if (flushing) return
  const headers = authHeaders()
  if (!headers) return
  const queue = readQueue()
  if (!queue.length) return
  flushing = true
  const batch = queue.splice(0, BATCH_SIZE)
  writeQueue(queue)
  try {
    const response = await fetch(endpointUrl('/analytics/events'), {
      method: 'POST',
      headers,
      body: JSON.stringify({events: batch}),
      keepalive,
    })
    if (!response.ok) throw new Error(`analytics_${response.status}`)
  } catch (_) {
    writeQueue([...batch, ...readQueue()].slice(-MAX_QUEUE))
  } finally {
    flushing = false
  }
}

const screenName = (path: string) => analyticsPageName(
  path, document.querySelector('h1')?.textContent?.trim() || document.title
)

const sendHeartbeat = async () => {
  const headers = authHeaders()
  if (!headers) return
  try {
    await fetch(endpointUrl('/analytics/heartbeat'), {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify({
        ...context(),
        screen_name: screenName(currentPath),
        is_visible: document.visibilityState === 'visible',
        session_started_at: getSessionStartedAt(),
        occurred_at: new Date().toISOString(),
      }),
    })
  } catch (_) {}
}

export const trackPageView = (path: string) => {
  const nextPath = normalizePagePath(path)
  const now = performance.now()
  if (currentPath && currentScreenStartedAt) {
    enqueueAnalyticsEvent({
      event_type: 'page_duration',
      event_name: screenName(currentPath),
      path: currentPath,
      duration_ms: Math.max(0, now - currentScreenStartedAt),
      success: true,
    })
  }
  currentPath = nextPath
  currentScreenStartedAt = now
  enqueueAnalyticsEvent({
    event_type: 'page_view',
    event_name: screenName(nextPath),
    path: nextPath,
    referrer: lastReferrer,
    success: true,
  })
  lastReferrer = window.location.href
  sendHeartbeat()
}

export const trackFeature = (name: string, metadata?: Record<string, unknown>) => {
  if (!isMeaningfulAnalyticsLabel(name)) return
  enqueueAnalyticsEvent({event_type: 'feature_use', event_name: name, success: true, metadata})
}

const installClickTracking = () => {
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>('button, a, [role="button"], [data-analytics-feature]')
      : null
    if (!target || target.closest('[data-analytics-ignore]')) return
    const explicitName = target.dataset.analyticsFeature
    const accessibleName = target.getAttribute('aria-label') || target.getAttribute('title')
    const visibleName = target.textContent?.replace(/\s+/g, ' ').trim().slice(0, 120)
    let name = explicitName || accessibleName || visibleName
    if (!isMeaningfulAnalyticsLabel(name)) {
      name = target instanceof HTMLAnchorElement && target.pathname
        ? `Acessar ${analyticsPageName(normalizePagePath(target.pathname)).toLocaleLowerCase('pt-BR')}`
        : undefined
    }
    if (!name || !isMeaningfulAnalyticsLabel(name)) return
    enqueueAnalyticsEvent({
      event_type: 'feature_use',
      event_name: name,
      success: true,
      metadata: {
        tag: target.tagName.toLowerCase(),
        href: target instanceof HTMLAnchorElement ? normalizePagePath(target.pathname) : undefined,
        automatic: !explicitName,
      },
    })
  }, {capture: true})
}

const installAxiosTracking = () => {
  axios.interceptors.request.use((config: any) => {
    if (!isAnalyticsUrl(config.url)) {
      config.__analyticsStartedAt = performance.now()
      config.headers = config.headers || {}
      config.headers['X-Analytics-Session-Id'] = getSessionId()
      config.headers['X-Analytics-Path'] = currentPath
    }
    return config
  })
  axios.interceptors.response.use(
    (response) => {
      const config: any = response.config
      if (!isAnalyticsUrl(config.url) && config.__analyticsStartedAt) {
        enqueueAnalyticsEvent({
          event_type: 'client_api_request', event_name: normalizeEndpoint(config.url),
          method: String(config.method || 'GET').toUpperCase(), status: response.status,
          duration_ms: performance.now() - config.__analyticsStartedAt,
          success: response.status < 400, metadata: {source: 'browser'},
        })
      }
      return response
    },
    (error) => {
      const config: any = error?.config || {}
      if (!isAnalyticsUrl(config.url) && config.__analyticsStartedAt) {
        enqueueAnalyticsEvent({
          event_type: 'client_api_request', event_name: normalizeEndpoint(config.url),
          method: String(config.method || 'GET').toUpperCase(), status: error?.response?.status,
          duration_ms: performance.now() - config.__analyticsStartedAt, success: false,
          metadata: {source: 'browser', network_error: !error?.response},
        })
      }
      return Promise.reject(error)
    }
  )
}

const installErrorTracking = () => {
  window.addEventListener('error', (event) => {
    enqueueAnalyticsEvent({
      event_type: 'javascript_error', event_name: event.error?.name || 'JavaScript Error',
      success: false, metadata: {message: String(event.message || '').slice(0, 500), filename: event.filename, line: event.lineno, column: event.colno},
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason.message : String(event.reason || '')
    enqueueAnalyticsEvent({
      event_type: 'unhandled_rejection', event_name: 'Promise rejeitada', success: false,
      metadata: {message: reason.slice(0, 500)},
    })
  })
}

const observeWebVitals = () => {
  try {
    const paint = performance.getEntriesByName('first-contentful-paint')[0]
    if (paint) enqueueAnalyticsEvent({event_type: 'web_vital', event_name: 'FCP', duration_ms: paint.startTime, success: true})
    let lcp = 0
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      lcp = entries[entries.length - 1]?.startTime || lcp
    })
    lcpObserver.observe({type: 'largest-contentful-paint', buffered: true} as any)
    let cls = 0
    const clsObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) cls += entry.value || 0
      })
    })
    clsObserver.observe({type: 'layout-shift', buffered: true} as any)
    const report = () => {
      if (lcp) enqueueAnalyticsEvent({event_type: 'web_vital', event_name: 'LCP', duration_ms: lcp, success: true})
      enqueueAnalyticsEvent({event_type: 'web_vital', event_name: 'CLS', duration_ms: cls, success: true})
      lcpObserver.disconnect()
      clsObserver.disconnect()
    }
    window.addEventListener('pagehide', report, {once: true})
  } catch (_) {}
}

const collectNavigationMetrics = () => {
  window.setTimeout(() => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (!navigation) return
    enqueueAnalyticsEvent({
      event_type: 'navigation_timing', event_name: 'Carregamento inicial',
      duration_ms: navigation.loadEventEnd || navigation.duration, success: true,
      metadata: {
        ttfb: navigation.responseStart,
        dom_interactive: navigation.domInteractive,
        dom_content_loaded: navigation.domContentLoadedEventEnd,
        load: navigation.loadEventEnd,
      },
    })
  }, 0)
}

export const startAnalytics = () => {
  if (started || !getAuth()?.api_token) return
  started = true
  getSessionId()
  installClickTracking()
  installAxiosTracking()
  installErrorTracking()
  observeWebVitals()
  collectNavigationMetrics()
  enqueueAnalyticsEvent({event_type: 'session_start', event_name: 'Sessão iniciada', success: true})
  sendHeartbeat()
  flushTimer = window.setInterval(() => flushAnalytics(), FLUSH_INTERVAL)
  heartbeatTimer = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)
  document.addEventListener('visibilitychange', sendHeartbeat)
  window.addEventListener('pagehide', () => {
    const elapsed = Math.max(0, performance.now() - currentScreenStartedAt)
    enqueueAnalyticsEvent({event_type: 'page_duration', event_name: screenName(currentPath), duration_ms: elapsed, success: true})
    enqueueAnalyticsEvent({event_type: 'session_end', event_name: 'Sessão encerrada', success: true})
    flushAnalytics(true)
  })
}

export const stopAnalytics = () => {
  if (flushTimer) window.clearInterval(flushTimer)
  if (heartbeatTimer) window.clearInterval(heartbeatTimer)
  started = false
}
